import axios from "axios";
import { useSnackbar } from "notistack";
import { useState, useCallback, ReactElement, useEffect } from "react";
import { Control, FieldValues } from "react-hook-form";

import {
  CreateUploadMutation,
  CreateUploadMutationVariables,
  UploadType,
  UploadVisibility,
} from "~frontend/__generated__/graphql";
import Loading from "~frontend/components/Loading";
import { CREATE_UPLOAD } from "~frontend/graphql/upload";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { parseCsv } from "~frontend/util/csv";
import useMutation from "~frontend/util/useMutation";
import usePrevious from "~frontend/util/usePrevious";

import FilePicker from "../FilePicker";
import {
  ParsedFile,
  getDefaultPhoneNumberColumn,
  ColumnPickerField,
  getDefaultColumnPickerFieldColumn,
} from "./ParsedFile";
import Uploading from "./Uploading";

export type { ColumnPickerField };

const MAX_FILE_SIZE_MB = 1000;

export type CsvProcessingState =
  | "IMPORTING_CSV"
  | "CSV_IMPORTED"
  | "CSV_IMPORT_FAILED";

export type CsvState =
  | "NO_CSV"
  | "PARSING_CSV"
  | "PARSED_CSV_ERROR"
  | "PARSED_CSV"
  | "UPLOADING_CSV"
  | "UPLOADED_CSV"
  | CsvProcessingState;

export type CSVMetadataValid = {
  valid: true;
  rows: number;
  columns: string[];
  sampleRows: { [key: string]: string }[];
};

export type CSVMetadataInvalid = {
  valid: false;
  errors: string[];
};

type CSVMetadata = CSVMetadataValid | CSVMetadataInvalid;

export const useCsvUpload = <T extends FieldValues = FieldValues>({
  uploadType,
  overwriting,
  onChangeOverwriting,
  renderProcessingState,
  doNotRenderFileWhileImporting,
  allowPhoneNumberOnlyColumn = false,
  overrideContactLimit,
  validCsvFilename,
  columnPickerFields = [],
  rhfControl,
}: {
  uploadType: UploadType;
  overwriting?: boolean;
  onChangeOverwriting?: (newValue: boolean) => void;
  renderProcessingState: (onClearFile: () => void) => ReactElement | null;
  doNotRenderFileWhileImporting?: boolean;
  allowPhoneNumberOnlyColumn?: boolean;
  overrideContactLimit?: number;
  validCsvFilename?: (
    filename: string
  ) => { valid: true } | { valid: false; errorMessage: string };
  columnPickerFields?: ColumnPickerField[];
  rhfControl?: Control<T>;
}): {
  csvState: CsvState;
  isOverwritingInProgress: boolean;
  isProcessing: boolean;
  startCsvUpload: ({
    phoneNumberColumn,
  }: {
    phoneNumberColumn?: string;
  }) => Promise<{
    estimatedRowCount: number;
    uploadToken: string;
    phoneNumberColumn: string;
  } | null>;
  renderCsvUpload: (
    setFieldValue?: (field: string, name: string) => void,
    includeFirstNameLastNameColumns?: boolean
  ) => ReactElement;
  onClearFile: () => void;
  setProcessingState: (state: CsvProcessingState) => void;
  startManualCsvUpload: ({ generatedCsv }: { generatedCsv: File }) => Promise<{
    estimatedRowCount: number;
    uploadToken: string;
    phoneNumberColumn: string;
  } | null>;
} => {
  const subaccountInfo = useSubaccount();

  const [csvState, setCsvState] = useState<CsvState>("NO_CSV");

  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvParseProgress, setCsvParseProgress] = useState(0);
  const [csvUploadProgress, setCsvUploadProgress] = useState(0);
  const [csvMetadata, setCsvMetadata] = useState<CSVMetadata | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const onClearFile = useCallback(() => {
    setCsvFile(null);
    setCsvState("NO_CSV");
  }, []);

  const setProcessingState = useCallback(
    (state: CsvProcessingState) => {
      setCsvState(state);
    },
    [setCsvState]
  );

  const defaultContactLimit = useFeatureFlag("contactLimit");
  const contactLimit = overrideContactLimit ?? defaultContactLimit;

  const isProcessing = [
    "IMPORTING_CSV",
    "CSV_IMPORTED",
    "CSV_IMPORT_FAILED",
  ].includes(csvState);

  const previousCsvState = usePrevious<CsvState>(csvState);
  useEffect(() => {
    if (
      ["CSV_IMPORTED", "CSV_IMPORT_FAILED"].includes(previousCsvState) &&
      csvState === "NO_CSV"
    ) {
      onChangeOverwriting?.(true);
    }
  }, [csvState, onChangeOverwriting, previousCsvState]);

  const [createUpload] = useMutation<
    CreateUploadMutation,
    CreateUploadMutationVariables
  >(CREATE_UPLOAD, {
    variables: {
      input: {
        subaccountId: subaccountInfo?.subaccount.id,
        type: uploadType,
        contentType: "application/csv",
        fileName: csvFile?.name ?? "import.csv",
        visibility: UploadVisibility.Private,
      },
    },
    onErrorSnackbarMessage: "Error uploading CSV",
  });

  const startCsvUpload = useCallback(
    async ({ phoneNumberColumn }: { phoneNumberColumn?: string }) => {
      setCsvState("UPLOADING_CSV");
      setCsvUploadProgress(0);

      const createUploadResponse = await createUpload();
      if (createUploadResponse.errors || !createUploadResponse.data) {
        console.error(createUploadResponse.errors);
        enqueueSnackbar("Failed to start upload", { variant: "error" });
        setCsvState("PARSED_CSV");
        return null;
      }

      const uploadUrl = createUploadResponse.data.createUpload.presignedPutUrl;
      try {
        await axios.put(uploadUrl, csvFile, {
          onUploadProgress: event => {
            if (event.total) {
              setCsvUploadProgress(event.loaded / event.total);
            }
          },
          headers: {
            "Content-Type": "application/csv",
          },
        });
      } catch (e) {
        console.error(e);
        setCsvState("PARSED_CSV");
        enqueueSnackbar("Failed to upload CSV", { variant: "error" });
        return null;
      }

      const uploadToken = createUploadResponse.data.createUpload.token;

      setCsvState("UPLOADED_CSV");

      onChangeOverwriting?.(false);

      const phoneColumn =
        phoneNumberColumn ??
        getDefaultPhoneNumberColumn(csvMetadata as CSVMetadataValid);

      return {
        estimatedRowCount: (csvMetadata as CSVMetadataValid).rows,
        uploadToken,
        phoneNumberColumn: phoneColumn!,
      };
    },
    [
      csvFile,
      csvMetadata,
      setCsvState,
      setCsvUploadProgress,
      createUpload,
      enqueueSnackbar,
      onChangeOverwriting,
    ]
  );

  const startManualCsvUpload = useCallback(
    async ({ generatedCsv }: { generatedCsv: File }) => {
      setCsvFile(generatedCsv);
      const metadata = await parseCsv(generatedCsv);

      const createUploadResponse = await createUpload();
      if (createUploadResponse.errors || !createUploadResponse.data) {
        console.error(createUploadResponse.errors);
        enqueueSnackbar("Failed to start upload", { variant: "error" });
        return null;
      }

      const uploadUrl = createUploadResponse.data.createUpload.presignedPutUrl;
      try {
        await axios.put(uploadUrl, generatedCsv, {
          headers: {
            "Content-Type": "application/csv",
          },
        });
      } catch (e) {
        console.error(e);
        enqueueSnackbar("Failed to upload CSV", { variant: "error" });
        return null;
      }

      const uploadToken = createUploadResponse.data.createUpload.token;

      return {
        estimatedRowCount: (metadata as CSVMetadataValid).rows,
        uploadToken,
        phoneNumberColumn: "phoneNumber",
      };
    },
    [createUpload, enqueueSnackbar, setCsvFile]
  );

  const handleSelectCsv = useCallback(
    async (
      file: File,
      setFieldValue?: (field: string, value: string) => void
    ) => {
      setCsvFile(file);
      setCsvState("PARSING_CSV");

      if (validCsvFilename) {
        const filename = validCsvFilename(file.name);
        if (!filename.valid) {
          setCsvMetadata({
            valid: false,
            errors: [filename.errorMessage],
          });
          setCsvState("PARSED_CSV_ERROR");
          return;
        }
      }

      const metadata = await parseCsv(file, setCsvParseProgress);

      if (!metadata.valid) {
        setCsvMetadata(metadata);
        setCsvState("PARSED_CSV_ERROR");
      } else if (metadata.rows > contactLimit) {
        setCsvMetadata({
          ...metadata,
          valid: false,
          errors: [
            `CSV exceeds import limit of ${contactLimit.toLocaleString()} rows. Please upload a CSV below this row size limit.`,
          ],
        });
        setCsvState("PARSED_CSV_ERROR");
      } else {
        setCsvMetadata(metadata);
        setCsvState("PARSED_CSV");

        if (setFieldValue) {
          setFieldValue(
            "phoneNumberColumn",
            getDefaultPhoneNumberColumn(metadata) || ""
          );

          columnPickerFields.forEach(field => {
            setFieldValue(
              field.name,
              getDefaultColumnPickerFieldColumn(field, metadata) || ""
            );
          });
        }
      }
    },
    [
      setCsvFile,
      setCsvState,
      contactLimit,
      validCsvFilename,
      columnPickerFields,
    ]
  );

  const renderCsvUpload = useCallback(
    (setFieldValue?: (field: string, value: string) => void) => {
      switch (csvState) {
        case "NO_CSV":
          return (
            <FilePicker
              dropzoneText="Drop a CSV here or click to select a file"
              onPickFile={(file: File) => handleSelectCsv(file, setFieldValue)}
              accept={{ "text/csv": [".csv"] }}
              maxFiles={1}
              maxSizeMB={MAX_FILE_SIZE_MB}
            />
          );
        case "PARSING_CSV":
          return (
            <Loading
              message="Validating CSV"
              progress={csvParseProgress * 100}
            />
          );
        case "UPLOADING_CSV":
          return (
            <Uploading progress={csvUploadProgress} filename={csvFile?.name} />
          );
        case "PARSED_CSV":
        case "PARSED_CSV_ERROR":
        default: {
          // Sometimes we accept a CSV with only phone numbers, so use this flag to
          // indicate we don't need the users to explicitly tell us which column is the phone number column
          // if there is only one column in the file
          const hidePhoneNumberField =
            allowPhoneNumberOnlyColumn &&
            csvMetadata!.valid &&
            csvMetadata!.columns.length === 1;

          return (
            <>
              {csvMetadata &&
                (csvState !== "IMPORTING_CSV" ||
                  !doNotRenderFileWhileImporting) && (
                  <ParsedFile
                    name="phoneNumberColumn"
                    hidePhoneNumberField={hidePhoneNumberField}
                    fileName={csvFile!.name}
                    metadata={csvMetadata!}
                    onClearFile={onClearFile}
                    columnPickerFields={columnPickerFields}
                    rhfControl={rhfControl as Control<FieldValues>}
                  />
                )}
              {renderProcessingState(onClearFile)}
            </>
          );
        }
      }
    },
    [
      csvState,
      csvUploadProgress,
      renderProcessingState,
      csvParseProgress,
      csvMetadata,
      csvFile,
      handleSelectCsv,
      onClearFile,
      allowPhoneNumberOnlyColumn,
      doNotRenderFileWhileImporting,
      columnPickerFields,
      rhfControl,
    ]
  );

  return {
    csvState,
    isOverwritingInProgress:
      (overwriting &&
        ["PARSING_CSV", "PARSED_CSV", "UPLOADING_CSV"].includes(csvState)) ||
      false,
    isProcessing,
    startCsvUpload,
    renderCsvUpload,
    onClearFile,
    setProcessingState,
    startManualCsvUpload,
  };
};
